import { Button, FormControl, FormLabel, Heading } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import IconGenerateReport from 'assets/icons/icon-file-paper-line.svg?react';
import CardForm from 'components/ui/CardForm';
import CheckboxSelect from 'components/ui/CheckboxSelect/CheckboxSelect';
import CustomRangeDatepicker from 'components/ui/Datepicker/CustomRangeDatepicker';
import FormControlGroup from 'components/ui/FormControlGroup';
import config from 'config';
import { useMessages } from 'hooks/useMessages';
import { useResourceList } from 'hooks/useResourceList';
import { ArrayRequiredMinLenTestFunction } from 'lib/validation';
import {
  FormProvider,
  Resolver,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import * as Yup from 'yup';
import { useAnalyticsReport } from '../hooks/useAnalyticsReport';

interface FormValues {
  companyIds: string[];
  dates: Date[];
}

const validationSchema = Yup.object().shape({
  companyIds: Yup.array(Yup.string()).optional(),
  dates: Yup.mixed().test('dates', '', ArrayRequiredMinLenTestFunction(2)),
});

export default function TabAnalyticsReport() {
  const { onError } = useMessages();

  const { data: companies } = useResourceList({
    resourceName: 'companies',
  });

  const methods = useForm<FormValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema) as unknown as Resolver<FormValues>,
    defaultValues: {
      companyIds: [],
      dates: [],
    },
  });

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors, isValid },
  } = methods;

  const { onGenerateAnalyticsReport, isPending } = useAnalyticsReport({
    onSuccess: (filepath: string) => {
      window.location.assign(`${config.api}/${filepath}`);
    },
    onError: (error) => onError(error?.response?.data?.message),
  });

  const onSubmit: SubmitHandler<FormValues> = ({ companyIds, dates }) => {
    const [startDate, endDate] = dates;

    onGenerateAnalyticsReport({ companyIds, startDate, endDate });
  };

  const companyOptions = companies?.map((company) => ({
    value: company.id,
    label: company.name,
  }));

  const selectedDates = watch('dates');

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardForm
          boxShadow="none"
          border="1px solid #E2E2E2"
          bgColor="rgba(240, 241, 242, 0.60)"
        >
          <Heading as="h3" size="h3">
            Gerar novo relatório
            <span>Preencha os filtros para gerar um novo relatório</span>
          </Heading>

          <FormControlGroup mt="25px" mr="auto">
            <FormControl isInvalid={!!errors.dates} isRequired>
              <FormLabel>Período</FormLabel>
              <CustomRangeDatepicker
                dates={selectedDates ?? []}
                onDateChange={(dates: Date[]) =>
                  setValue('dates', dates, { shouldValidate: true })
                }
              />
            </FormControl>

            <FormControl isInvalid={!!errors.companyIds}>
              <FormLabel>Clientes</FormLabel>
              <CheckboxSelect
                placeholder="Todos os clientes"
                {...register('companyIds')}
                onChange={(value) =>
                  setValue(
                    'companyIds',
                    value?.map((v) => v.value)
                  )
                }
                options={companyOptions}
              />
            </FormControl>
          </FormControlGroup>

          <Button
            variant="primary"
            type="submit"
            ml="auto"
            rightIcon={<IconGenerateReport />}
            isDisabled={isPending || !isValid}
            isLoading={isPending}
          >
            Gerar relatório
          </Button>
        </CardForm>
      </form>
    </FormProvider>
  );
}
